import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CottageOutlinedIcon from '@mui/icons-material/CottageOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import PoolOutlinedIcon from '@mui/icons-material/PoolOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import HomeRepairServiceOutlinedIcon from '@mui/icons-material/HomeRepairServiceOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';
import DomainAddOutlinedIcon from '@mui/icons-material/DomainAddOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { PERMISSIONS } from '@/permissions/permissionRegistry';
import { UNIQUE_COMMITTEE_OFFICES } from '@/constants/committee';
import { ROLES } from '@/constants';
import type {
  PropertyStructureConfig,
  ResolvedPropertyTerminology,
} from '@/constants/propertyStructure';
import { resolveLocalizedPropertyTerm } from '@/i18n/terminology';
import type { SupportedLanguage } from '@/i18n/types';

export interface MenuItemConfig {
  id: string;
  label: string;
  translationKey: string;
  path?: string;
  icon: ComponentType<SvgIconProps>;
  /** Hierarchical feature key checked against Society Access Control (e.g. 'finance.expenses'). */
  featureKey?: string;
  /** All listed permissions must be granted (AND). */
  permission?: string | string[];
  /** At least one listed permission must be granted (OR). */
  anyOf?: string[];
  /** Legacy role-based gate, evaluated alongside permission/anyOf (OR). */
  roles?: string[];
  /** When true, Super Admin does not bypass `roles`. */
  strictRoles?: boolean;
  /** Platform operator (VYORA owner) vs society workspace. Default: society if gated, both if ungated dashboard. */
  audience?: 'platform' | 'society' | 'both';
  /** Shown while the society is license-locked (menus without this are hidden). */
  whenLocked?: boolean;
  /** Hidden unless the society is license-locked. */
  whenLockedOnly?: boolean;
  /** Property capabilities required to display this menu (e.g. 'block' | 'tower' | 'flat' | 'house' | 'plot' | 'road'). */
  propertyCapability?: 'road' | 'block' | 'tower' | 'flat' | 'house' | 'plot' | Array<'road' | 'block' | 'tower' | 'flat' | 'house' | 'plot'>;
  /** Restrict menu to specific society types if needed. */
  allowedSocietyTypes?: string[];
  /** Default presentation order in the registry. */
  defaultOrder?: number;
  /** Whether society admin can customize/reorder this menu. */
  isCustomizable?: boolean;
  children?: MenuItemConfig[];
}

/**
 * Declarative sidebar menu organized logically around society management workflows.
 * Super Admin is the VYORA product owner (societies and software licenses), not a society office-bearer.
 */
export const MENU_CONFIG: MenuItemConfig[] = [
  // 1. Overview
  {
    id: 'dashboard',
    label: 'Dashboard',
    translationKey: 'nav.dashboard',
    path: '/dashboard',
    icon: DashboardOutlinedIcon,
    featureKey: 'dashboard',
    audience: 'both',
    defaultOrder: 1,
  },

  // 2. Society & Properties Management
  {
    id: 'society',
    label: 'Society',
    translationKey: 'nav.society',
    icon: ApartmentOutlinedIcon,
    featureKey: 'society',
    defaultOrder: 2,
    anyOf: [
      PERMISSIONS.society.read,
      PERMISSIONS.house.read,
      PERMISSIONS.resident.read,
      PERMISSIONS.staff.read,
      PERMISSIONS.house.create,
    ],
    children: [
      {
        id: 'society-settings',
        label: 'My Society',
        translationKey: 'nav.societySettings',
        path: '/society',
        icon: StorefrontOutlinedIcon,
        featureKey: 'society.my_society',
        permission: PERMISSIONS.society.read,
        defaultOrder: 1,
      },
      {
        id: 'society-blocks',
        label: 'Blocks / Towers',
        translationKey: 'nav.societyBlocks',
        path: '/blocks',
        icon: DomainOutlinedIcon,
        featureKey: 'society.blocks',
        permission: PERMISSIONS.house.read,
        propertyCapability: ['block', 'tower'],
        defaultOrder: 2,
      },
      {
        id: 'society-houses',
        label: 'Houses',
        translationKey: 'nav.societyHouses',
        path: '/houses',
        icon: CottageOutlinedIcon,
        featureKey: 'society.houses',
        permission: PERMISSIONS.house.read,
        propertyCapability: ['flat', 'house', 'plot'],
        defaultOrder: 3,
      },
      {
        id: 'society-residents',
        label: 'Residents',
        translationKey: 'nav.societyResidents',
        path: '/residents',
        icon: PeopleAltOutlinedIcon,
        featureKey: 'society.residents',
        permission: PERMISSIONS.resident.read,
        defaultOrder: 4,
      },
      {
        id: 'society-staff',
        label: 'Staff',
        translationKey: 'nav.societyStaff',
        path: '/staff',
        icon: BadgeOutlinedIcon,
        featureKey: 'society.staff',
        permission: PERMISSIONS.staff.read,
        defaultOrder: 5,
      },
      {
        id: 'imports',
        label: 'Import Data',
        translationKey: 'nav.imports',
        path: '/import',
        icon: CloudUploadOutlinedIcon,
        featureKey: 'society.import',
        anyOf: [PERMISSIONS.house.create, PERMISSIONS.resident.create, PERMISSIONS.expense.create],
        defaultOrder: 6,
      },
    ],
  },

  // 3. Finance & Accounts
  {
    id: 'finance',
    label: 'Finance',
    translationKey: 'nav.finance',
    icon: AccountBalanceWalletOutlinedIcon,
    featureKey: 'finance',
    defaultOrder: 3,
    anyOf: [
      PERMISSIONS.finance.read,
      PERMISSIONS.corpus.read,
      PERMISSIONS.collection.read,
      PERMISSIONS.reimbursement.read,
      PERMISSIONS.expense.read,
    ],
    children: [
      {
        id: 'finance-payments',
        label: 'My payments',
        translationKey: 'nav.financePayments',
        path: '/dues',
        icon: PaymentOutlinedIcon,
        featureKey: 'finance.maintenance',
        permission: PERMISSIONS.collection.read,
        defaultOrder: 1,
      },
      {
        id: 'finance-collections',
        label: 'Collections',
        translationKey: 'nav.financeCollections',
        path: '/collections',
        icon: ReceiptLongOutlinedIcon,
        featureKey: 'finance.collections',
        permission: PERMISSIONS.collection.read,
        defaultOrder: 2,
      },
      {
        id: 'finance-society-fund',
        label: 'Society Fund',
        translationKey: 'nav.financeSocietyFund',
        path: '/collections/society-fund',
        icon: SavingsOutlinedIcon,
        featureKey: 'finance.society_fund',
        permission: PERMISSIONS.collection.create,
        defaultOrder: 3,
      },
      {
        id: 'finance-special-collections',
        label: 'Special collections',
        translationKey: 'nav.financeSpecialCollections',
        path: '/collections/special-collections',
        icon: PriceCheckOutlinedIcon,
        featureKey: 'finance.special_collections',
        permission: PERMISSIONS.collection.create,
        defaultOrder: 4,
      },
      {
        id: 'finance-corpus-fund',
        label: 'Corpus Fund',
        translationKey: 'nav.financeCorpusFund',
        path: '/finance/corpus-fund',
        icon: SavingsOutlinedIcon,
        featureKey: 'finance.corpus_fund',
        permission: PERMISSIONS.corpus.read,
        defaultOrder: 5,
      },
      {
        id: 'finance-expenses',
        label: 'Expenses',
        translationKey: 'nav.financeExpenses',
        path: '/expenses',
        icon: TrendingDownOutlinedIcon,
        featureKey: 'finance.expenses',
        permission: PERMISSIONS.expense.read,
        defaultOrder: 6,
      },
      {
        id: 'finance-expense-groups',
        label: 'Expense Groups',
        translationKey: 'nav.financeExpenseGroups',
        path: '/expenses/groups',
        icon: GroupsOutlinedIcon,
        featureKey: 'finance.expense_groups',
        permission: PERMISSIONS.expense.read,
        defaultOrder: 7,
      },
      {
        id: 'finance-water-tanker',
        label: 'Water Tanker',
        translationKey: 'nav.financeWaterTanker',
        path: '/water-tanker',
        icon: WaterDropOutlinedIcon,
        featureKey: 'finance.water_tanker',
        anyOf: [PERMISSIONS.expense.read, PERMISSIONS.society.read],
        defaultOrder: 8,
      },
      {
        id: 'finance-reimbursements',
        label: 'Claim reimbursement',
        translationKey: 'nav.financeReimbursements',
        path: '/reimbursements',
        icon: RequestQuoteOutlinedIcon,
        featureKey: 'finance.reimbursements',
        permission: PERMISSIONS.reimbursement.read,
        defaultOrder: 9,
      },
      {
        id: 'finance-banking',
        label: 'Bank Accounts',
        translationKey: 'nav.financeBanking',
        path: '/banking',
        icon: AccountBalanceOutlinedIcon,
        featureKey: 'finance.banking',
        permission: PERMISSIONS.finance.read,
        defaultOrder: 10,
      },
      {
        id: 'finance-statements',
        label: 'Statements & Receipts',
        translationKey: 'nav.financeStatements',
        path: '/finance/statements',
        icon: ReceiptLongOutlinedIcon,
        featureKey: 'finance.receipts',
        permission: PERMISSIONS.finance.read,
        defaultOrder: 11,
      },
      {
        id: 'finance-reports',
        label: 'Financial Reports',
        translationKey: 'nav.financeReports',
        path: '/finance/reports',
        icon: AssessmentOutlinedIcon,
        featureKey: 'finance.reports',
        permission: PERMISSIONS.finance.read,
        defaultOrder: 12,
      },
      {
        id: 'finance-reconciliation',
        label: 'Payment Reconciliation',
        translationKey: 'nav.financeReconciliation',
        path: '/finance/reconciliation',
        icon: PendingActionsOutlinedIcon,
        featureKey: 'finance.reconciliation',
        permission: PERMISSIONS.finance.read,
        defaultOrder: 13,
      },
    ],
  },

  // 4. Security & Gate Access
  {
    id: 'security-admin',
    label: 'Security & Gate',
    translationKey: 'nav.security',
    icon: ShieldOutlinedIcon,
    featureKey: 'security',
    defaultOrder: 4,
    anyOf: [PERMISSIONS.visitor.read, PERMISSIONS.gatepass.view, PERMISSIONS.society.update],
    roles: [
      'WATCHMAN',
      ROLES.PRESIDENT,
      ROLES.VICE_PRESIDENT,
      ROLES.GENERAL_SECRETARY,
      ROLES.TREASURER,
      ROLES.EXECUTIVE_MEMBER,
      ROLES.OWNER,
      ROLES.RESIDENT,
    ],
    audience: 'society',
    children: [
      {
        id: 'gate-passes',
        label: 'Gate Passes',
        translationKey: 'nav.gatePasses',
        path: '/gate-passes',
        icon: QrCodeScannerIcon,
        featureKey: 'security.gate_pass',
        roles: [
          'RESIDENT',
          'OWNER',
          'WATCHMAN',
          'PRESIDENT',
          'VICE_PRESIDENT',
          'GENERAL_SECRETARY',
          'TREASURER',
          'EXECUTIVE_MEMBER',
        ],
        anyOf: [PERMISSIONS.gatepass.view, PERMISSIONS.gatepass.create, PERMISSIONS.visitor.read],
        audience: 'society',
        defaultOrder: 1,
      },
      {
        id: 'security-dashboard',
        label: 'Security Dashboard',
        translationKey: 'nav.securityDashboard',
        path: '/security/admin',
        icon: BarChartOutlinedIcon,
        featureKey: 'security.visitors',
        permission: PERMISSIONS.visitor.read,
        roles: [
          'WATCHMAN',
          'PRESIDENT',
          'VICE_PRESIDENT',
          'GENERAL_SECRETARY',
          'TREASURER',
          'EXECUTIVE_MEMBER',
        ],
        audience: 'society',
        defaultOrder: 2,
      },
      {
        id: 'security-gates',
        label: 'Gate Management',
        translationKey: 'nav.securityGates',
        path: '/security/gates',
        icon: MeetingRoomOutlinedIcon,
        featureKey: 'security.gates',
        permission: PERMISSIONS.society.update,
        roles: ['PRESIDENT', 'VICE_PRESIDENT', 'GENERAL_SECRETARY', 'TREASURER', 'ADMIN'],
        audience: 'society',
        defaultOrder: 3,
      },
      {
        id: 'security-shifts',
        label: 'Watchman Shift Reports',
        translationKey: 'nav.securityShifts',
        path: '/security/shifts',
        icon: BadgeOutlinedIcon,
        featureKey: 'security.shifts',
        permission: PERMISSIONS.visitor.read,
        roles: [
          'PRESIDENT',
          'VICE_PRESIDENT',
          'GENERAL_SECRETARY',
          'TREASURER',
          'EXECUTIVE_MEMBER',
          'ADMIN',
        ],
        audience: 'society',
        defaultOrder: 4,
      },
      {
        id: 'security-notification-settings',
        label: 'Security Policies',
        translationKey: 'nav.securityPolicies',
        path: '/security/notification-settings',
        icon: SettingsOutlinedIcon,
        featureKey: 'security.policies',
        permission: PERMISSIONS.society.update,
        roles: ['PRESIDENT', 'VICE_PRESIDENT', 'GENERAL_SECRETARY', 'TREASURER'],
        audience: 'society',
        defaultOrder: 5,
      },
      {
        id: 'security-desk-portal',
        label: 'Security Gate Desk',
        translationKey: 'nav.securityDeskPortal',
        path: '/watchman',
        icon: SecurityOutlinedIcon,
        featureKey: 'security.visitors',
        roles: ['WATCHMAN'],
        audience: 'society',
        defaultOrder: 6,
      },
      {
        id: 'visitors',
        label: 'Visitors Desk',
        translationKey: 'nav.visitors',
        path: '/visitors',
        icon: PeopleAltOutlinedIcon,
        featureKey: 'security.visitors',
        roles: ['WATCHMAN'],
        audience: 'society',
        defaultOrder: 7,
      },
      {
        id: 'security-cc-cameras',
        label: 'CC Cameras',
        translationKey: 'nav.securityCameras',
        path: '/security/cameras',
        icon: VideocamOutlinedIcon,
        featureKey: 'security.cc_cameras',
        permission: PERMISSIONS.society.update,
        audience: 'society',
        defaultOrder: 8,
      },
    ],
  },

  // 5. Community & Communication
  {
    id: 'community',
    label: 'Community',
    translationKey: 'nav.community',
    icon: CampaignOutlinedIcon,
    featureKey: 'communication',
    defaultOrder: 5,
    anyOf: [PERMISSIONS.notice.read, PERMISSIONS.event.read, PERMISSIONS.whatsapp.read],
    audience: 'society',
    children: [
      {
        id: 'notices',
        label: 'Notices',
        translationKey: 'nav.notices',
        path: '/notices',
        icon: CampaignOutlinedIcon,
        featureKey: 'communication.notices',
        permission: PERMISSIONS.notice.read,
        defaultOrder: 1,
      },
      {
        id: 'forum',
        label: 'Community Forum',
        translationKey: 'nav.communityForum',
        path: '/forum',
        icon: ForumOutlinedIcon,
        featureKey: 'communication.forum',
        defaultOrder: 2,
      },
      {
        id: 'events-list',
        label: 'Events',
        translationKey: 'nav.eventsList',
        path: '/events',
        icon: CelebrationOutlinedIcon,
        featureKey: 'communication.events',
        permission: PERMISSIONS.event.read,
        defaultOrder: 3,
      },
      {
        id: 'events-calendar',
        label: 'Event Calendar',
        translationKey: 'nav.eventsCalendar',
        path: '/events/calendar',
        icon: CalendarMonthOutlinedIcon,
        featureKey: 'communication.events',
        permission: PERMISSIONS.event.read,
        defaultOrder: 4,
      },
      {
        id: 'invitations',
        label: 'Invitations & RSVPs',
        translationKey: 'nav.invitations',
        path: '/invitations',
        icon: EmailOutlinedIcon,
        featureKey: 'communication.invitations',
        permission: PERMISSIONS.event.read,
        defaultOrder: 5,
      },
      {
        id: 'whatsapp',
        label: 'WhatsApp Logs',
        translationKey: 'nav.whatsapp',
        path: '/whatsapp/logs',
        icon: WhatsAppIcon,
        featureKey: 'communication.whatsapp',
        permission: PERMISSIONS.whatsapp.read,
        defaultOrder: 6,
      },
      {
        id: 'notifications',
        label: 'Notifications',
        translationKey: 'nav.notifications',
        path: '/notifications',
        icon: NotificationsNoneOutlinedIcon,
        defaultOrder: 7,
      },
    ],
  },

  // 6. Operations & Helpdesk
  {
    id: 'operations',
    label: 'Operations',
    translationKey: 'nav.operations',
    icon: HomeRepairServiceOutlinedIcon,
    featureKey: 'operations',
    defaultOrder: 6,
    anyOf: [PERMISSIONS.complaint.read, PERMISSIONS.service.read, PERMISSIONS.meeting.read],
    audience: 'society',
    children: [
      {
        id: 'complaints',
        label: 'Complaints',
        translationKey: 'nav.complaints',
        path: '/complaints',
        icon: ReportProblemOutlinedIcon,
        featureKey: 'operations.complaints',
        permission: PERMISSIONS.complaint.read,
        defaultOrder: 1,
      },
      {
        id: 'services-requests',
        label: 'Service Requests',
        translationKey: 'nav.serviceRequests',
        path: '/services/requests',
        icon: PendingActionsOutlinedIcon,
        featureKey: 'operations.services',
        permission: PERMISSIONS.service.read,
        defaultOrder: 2,
      },
      {
        id: 'services-directory',
        label: 'Service Directory',
        translationKey: 'nav.serviceDirectory',
        path: '/services',
        icon: ContactPhoneOutlinedIcon,
        featureKey: 'operations.services',
        permission: PERMISSIONS.service.read,
        defaultOrder: 3,
      },
      {
        id: 'amenities',
        label: 'Amenities & Facilities',
        translationKey: 'nav.amenities',
        path: '/amenities',
        icon: PoolOutlinedIcon,
        featureKey: 'operations.amenities',
        defaultOrder: 4,
      },
      {
        id: 'meetings',
        label: 'Meetings',
        translationKey: 'nav.meetings',
        path: '/meetings',
        icon: GroupsOutlinedIcon,
        featureKey: 'operations.meetings',
        permission: PERMISSIONS.meeting.read,
        defaultOrder: 5,
      },
      {
        id: 'documents',
        label: 'Document Vault',
        translationKey: 'nav.documents',
        path: '/documents',
        icon: FolderOpenOutlinedIcon,
        featureKey: 'operations.documents',
        permission: PERMISSIONS.document.read,
        defaultOrder: 6,
      },
    ],
  },

  // 7. Governance & Elections
  {
    id: 'governance',
    label: 'Governance',
    translationKey: 'nav.governance',
    icon: HowToVoteOutlinedIcon,
    featureKey: 'elections',
    defaultOrder: 7,
    audience: 'society',
    children: [
      {
        id: 'polls',
        label: 'Resident Polls',
        translationKey: 'nav.polls',
        path: '/polls',
        icon: AssessmentOutlinedIcon,
        featureKey: 'elections.polls',
        defaultOrder: 1,
      },
      {
        id: 'elections',
        label: 'Elections',
        translationKey: 'nav.elections',
        path: '/elections',
        icon: HowToVoteOutlinedIcon,
        featureKey: 'elections.voting',
        permission: PERMISSIONS.election.read,
        defaultOrder: 2,
      },
      {
        id: 'governance-petitions',
        label: 'File petition',
        translationKey: 'nav.governancePetitions',
        path: '/governance/petitions',
        icon: GavelOutlinedIcon,
        featureKey: 'elections.petitions',
        defaultOrder: 3,
      },
      {
        id: 'governance-resignation',
        label: 'Submit resignation',
        translationKey: 'nav.governanceResignation',
        path: '/governance/resignation',
        icon: AssignmentReturnOutlinedIcon,
        featureKey: 'elections.resignation',
        roles: [...UNIQUE_COMMITTEE_OFFICES],
        strictRoles: true,
        defaultOrder: 4,
      },
    ],
  },

  // 8. Administration
  {
    id: 'administration',
    label: 'Administration',
    translationKey: 'nav.administration',
    icon: AdminPanelSettingsOutlinedIcon,
    defaultOrder: 8,
    whenLocked: true,
    anyOf: [
      PERMISSIONS.role.read,
      PERMISSIONS.permission.read,
      PERMISSIONS.meeting.update,
      PERMISSIONS.society.update,
      PERMISSIONS.whatsapp.read,
      PERMISSIONS.whatsapp.update,
    ],
    roles: [ROLES.PRESIDENT, ROLES.VICE_PRESIDENT, ROLES.TREASURER],
    children: [
      {
        id: 'admin-license',
        label: 'License',
        translationKey: 'nav.adminLicense',
        path: '/settings/license',
        icon: WorkspacePremiumOutlinedIcon,
        whenLocked: true,
        roles: [ROLES.PRESIDENT, ROLES.TREASURER],
        defaultOrder: 1,
      },
      {
        id: 'admin-invoices',
        label: 'Subscription invoices',
        translationKey: 'nav.adminInvoices',
        path: '/settings/license/invoices',
        icon: ReceiptOutlinedIcon,
        whenLocked: true,
        roles: [ROLES.PRESIDENT, ROLES.TREASURER],
        defaultOrder: 2,
      },
      {
        id: 'admin-roles',
        label: 'Roles & Permissions',
        translationKey: 'nav.adminRoles',
        path: '/settings/roles',
        icon: ManageAccountsOutlinedIcon,
        roles: [ROLES.PRESIDENT, ROLES.VICE_PRESIDENT],
        defaultOrder: 3,
      },
      {
        id: 'admin-letterhead',
        label: 'Letterhead & Branding',
        translationKey: 'nav.adminLetterhead',
        path: '/settings/letterhead',
        icon: FolderOpenOutlinedIcon,
        anyOf: [PERMISSIONS.society.update, PERMISSIONS.society.read],
        roles: [ROLES.PRESIDENT, ROLES.VICE_PRESIDENT, ROLES.TREASURER],
        defaultOrder: 4,
      },
      {
        id: 'admin-integrations',
        label: 'SMS & WhatsApp',
        translationKey: 'nav.adminIntegrations',
        path: '/settings/integrations',
        icon: WhatsAppIcon,
        anyOf: [PERMISSIONS.society.update, PERMISSIONS.whatsapp.read, PERMISSIONS.whatsapp.update],
        defaultOrder: 5,
      },
      {
        id: 'admin-ai-config',
        label: 'AI Configuration',
        translationKey: 'nav.adminAiConfig',
        path: '/settings/ai-config',
        icon: AutoAwesomeOutlinedIcon,
        roles: [ROLES.PRESIDENT, ROLES.VICE_PRESIDENT],
        defaultOrder: 6,
      },
      {
        id: 'admin-navigation',
        label: 'Menu Customization',
        translationKey: 'nav.adminNavigation',
        path: '/settings/navigation',
        icon: HubOutlinedIcon,
        featureKey: 'society',
        anyOf: [PERMISSIONS.society.update, PERMISSIONS.society.read],
        roles: [ROLES.PRESIDENT, ROLES.VICE_PRESIDENT, ROLES.GENERAL_SECRETARY],
        defaultOrder: 7,
      },
      {
        id: 'admin-audit',
        label: 'Audit Logs',
        translationKey: 'nav.adminAudit',
        path: '/settings/audit',
        icon: HistoryOutlinedIcon,
        permission: PERMISSIONS.role.read,
        defaultOrder: 8,
      },
    ],
  },

  // 9. Documentation, QA & Support
  {
    id: 'documentation-group',
    label: 'Help & Documentation',
    translationKey: 'nav.documentationGroup',
    icon: MenuBookOutlinedIcon,
    defaultOrder: 9,
    audience: 'both',
    children: [
      {
        id: 'society-support-desk',
        label: 'Help & Support',
        translationKey: 'nav.societySupportDesk',
        path: '/support',
        icon: SupportAgentOutlinedIcon,
        audience: 'society',
        defaultOrder: 1,
      },
      {
        id: 'docs-knowledge-base',
        label: 'Knowledge Base',
        translationKey: 'nav.docsKnowledgeBase',
        path: '/documentation',
        icon: MenuBookOutlinedIcon,
        audience: 'both',
        defaultOrder: 2,
      },
      {
        id: 'docs-role-matrix',
        label: 'Role & Permission Matrix',
        translationKey: 'nav.docsRoleMatrix',
        path: '/documentation/role-matrix',
        icon: SecurityOutlinedIcon,
        audience: 'both',
        defaultOrder: 3,
      },
      {
        id: 'docs-qa-hub',
        label: 'QA Test Hub',
        translationKey: 'nav.docsQaHub',
        path: '/testing/dashboard',
        icon: BugReportOutlinedIcon,
        audience: 'platform',
        defaultOrder: 4,
      },
      {
        id: 'docs-test-explorer',
        label: 'Test Case Explorer',
        translationKey: 'nav.docsTestExplorer',
        path: '/testing/explorer',
        icon: BugReportOutlinedIcon,
        audience: 'platform',
        defaultOrder: 5,
      },
    ],
  },

  // 10. License Lock Screen
  {
    id: 'license-locked',
    label: 'License',
    translationKey: 'nav.licenseLocked',
    path: '/settings/license',
    icon: LockOutlinedIcon,
    whenLocked: true,
    whenLockedOnly: true,
    defaultOrder: 10,
  },

  // --- PLATFORM SUPER ADMIN MENUS (Unified Vyora Sidebar) ---
  {
    id: 'platform-societies-group',
    label: 'Societies & Tenants',
    translationKey: 'nav.platformSocietiesGroup',
    icon: DomainOutlinedIcon,
    audience: 'platform',
    defaultOrder: 101,
    children: [
      {
        id: 'platform-societies',
        label: 'All Societies',
        translationKey: 'nav.platformSocieties',
        path: '/super-admin/societies',
        icon: DomainOutlinedIcon,
        audience: 'platform',
        defaultOrder: 1,
      },
      {
        id: 'platform-societies-new',
        label: 'Register Society',
        translationKey: 'nav.platformSocietiesNew',
        path: '/super-admin/societies/new',
        icon: DomainAddOutlinedIcon,
        audience: 'platform',
        defaultOrder: 2,
      },
      {
        id: 'platform-society-types',
        label: 'Society Types & Taxonomy',
        translationKey: 'nav.platformSocietyTypes',
        path: '/super-admin/society-types',
        icon: HubOutlinedIcon,
        audience: 'platform',
        defaultOrder: 3,
      },
      {
        id: 'platform-trials',
        label: 'Free Trials & Onboarding',
        translationKey: 'nav.platformTrials',
        path: '/super-admin/trials',
        icon: PendingActionsOutlinedIcon,
        audience: 'platform',
        defaultOrder: 4,
      },
      {
        id: 'platform-subscriptions',
        label: 'Subscriptions',
        translationKey: 'nav.platformSubscriptions',
        path: '/super-admin/subscriptions',
        icon: ReceiptLongOutlinedIcon,
        audience: 'platform',
        defaultOrder: 5,
      },
      {
        id: 'platform-licenses',
        label: 'Software Licenses',
        translationKey: 'nav.platformLicenses',
        path: '/super-admin/licenses',
        icon: VpnKeyOutlinedIcon,
        audience: 'platform',
        defaultOrder: 6,
      },
      {
        id: 'platform-plans',
        label: 'Subscription Plans',
        translationKey: 'nav.platformPlans',
        path: '/super-admin/plans',
        icon: WorkspacePremiumOutlinedIcon,
        audience: 'platform',
        defaultOrder: 7,
      },
      {
        id: 'platform-invoices',
        label: 'Billing & Invoices',
        translationKey: 'nav.platformInvoices',
        path: '/super-admin/invoices',
        icon: ReceiptOutlinedIcon,
        audience: 'platform',
        defaultOrder: 8,
      },
      {
        id: 'platform-pricing-overrides',
        label: 'Pricing Overrides',
        translationKey: 'nav.platformPricingOverrides',
        path: '/super-admin/pricing-overrides',
        icon: LocalOfferOutlinedIcon,
        audience: 'platform',
        defaultOrder: 9,
      },
      {
        id: 'platform-coupons',
        label: 'Coupons & Discounts',
        translationKey: 'nav.platformCoupons',
        path: '/super-admin/coupons',
        icon: ConfirmationNumberOutlinedIcon,
        audience: 'platform',
        defaultOrder: 10,
      },
    ],
  },
  {
    id: 'platform-promotion-group',
    label: 'Growth & Promotion',
    translationKey: 'nav.platformPromotionGroup',
    icon: SendOutlinedIcon,
    audience: 'platform',
    defaultOrder: 102,
    children: [
      {
        id: 'platform-promotion-dashboard',
        label: 'Growth Dashboard',
        translationKey: 'nav.platformPromotionDashboard',
        path: '/super-admin/promotion',
        icon: DashboardOutlinedIcon,
        audience: 'platform',
        defaultOrder: 1,
      },
      {
        id: 'platform-promotion-prospects',
        label: 'Prospects & Leads',
        translationKey: 'nav.platformPromotionProspects',
        path: '/super-admin/promotion/prospects',
        icon: PeopleAltOutlinedIcon,
        audience: 'platform',
        defaultOrder: 2,
      },
      {
        id: 'platform-promotion-campaigns',
        label: 'Campaigns',
        translationKey: 'nav.platformPromotionCampaigns',
        path: '/super-admin/promotion/campaigns',
        icon: CampaignOutlinedIcon,
        audience: 'platform',
        defaultOrder: 3,
      },
      {
        id: 'platform-promotion-templates',
        label: 'Message Templates',
        translationKey: 'nav.platformPromotionTemplates',
        path: '/super-admin/promotion/templates',
        icon: DescriptionOutlinedIcon,
        audience: 'platform',
        defaultOrder: 4,
      },
      {
        id: 'platform-promotion-sender-profiles',
        label: 'Sender Profiles',
        translationKey: 'nav.platformPromotionSenderProfiles',
        path: '/super-admin/promotion/sender-profiles',
        icon: ContactMailOutlinedIcon,
        audience: 'platform',
        defaultOrder: 5,
      },
      {
        id: 'platform-promotion-email',
        label: 'Email Marketing',
        translationKey: 'nav.platformPromotionEmail',
        path: '/super-admin/promotion/email',
        icon: EmailOutlinedIcon,
        audience: 'platform',
        defaultOrder: 6,
      },
      {
        id: 'platform-promotion-whatsapp',
        label: 'WhatsApp Broadcasts',
        translationKey: 'nav.platformPromotionWhatsapp',
        path: '/super-admin/promotion/whatsapp',
        icon: WhatsAppIcon,
        audience: 'platform',
        defaultOrder: 7,
      },
      {
        id: 'platform-promotion-sms',
        label: 'SMS Marketing',
        translationKey: 'nav.platformPromotionSms',
        path: '/super-admin/promotion/sms',
        icon: SmsOutlinedIcon,
        audience: 'platform',
        defaultOrder: 8,
      },
      {
        id: 'platform-promotion-other',
        label: 'Other Channels',
        translationKey: 'nav.platformPromotionOther',
        path: '/super-admin/promotion/other-channels',
        icon: ExtensionOutlinedIcon,
        audience: 'platform',
        defaultOrder: 9,
      },
      {
        id: 'platform-referrals',
        label: 'Affiliates & Referrals',
        translationKey: 'nav.platformReferrals',
        path: '/super-admin/referrals',
        icon: ShareOutlinedIcon,
        audience: 'platform',
        defaultOrder: 10,
      },
      {
        id: 'platform-marketing',
        label: 'Marketing Personnel',
        translationKey: 'nav.platformMarketing',
        path: '/super-admin/marketing',
        icon: CampaignOutlinedIcon,
        audience: 'platform',
        defaultOrder: 11,
      },
      {
        id: 'platform-promotion-analytics',
        label: 'Promotion Analytics',
        translationKey: 'nav.platformPromotionAnalytics',
        path: '/super-admin/promotion/analytics',
        icon: InsightsOutlinedIcon,
        audience: 'platform',
        defaultOrder: 12,
      },
      {
        id: 'platform-promotion-settings',
        label: 'Marketing Settings',
        translationKey: 'nav.platformPromotionSettings',
        path: '/super-admin/promotion/settings',
        icon: SettingsOutlinedIcon,
        audience: 'platform',
        defaultOrder: 13,
      },
    ],
  },
  {
    id: 'platform-management-group',
    label: 'Platform & Support',
    translationKey: 'nav.platformManagementGroup',
    icon: AdminPanelSettingsOutlinedIcon,
    audience: 'platform',
    defaultOrder: 103,
    children: [
      {
        id: 'platform-website',
        label: 'Website CMS & Branding',
        translationKey: 'nav.platformWebsite',
        path: '/super-admin/website',
        icon: LanguageOutlinedIcon,
        audience: 'platform',
        defaultOrder: 1,
      },
      {
        id: 'platform-support-tickets',
        label: 'Support Tickets Desk',
        translationKey: 'nav.platformSupportTickets',
        path: '/super-admin/tickets',
        icon: SupportAgentOutlinedIcon,
        audience: 'platform',
        defaultOrder: 2,
      },
      {
        id: 'platform-feature-requests',
        label: 'Feature Requests',
        translationKey: 'nav.platformFeatureRequests',
        path: '/super-admin/feature-requests',
        icon: ExtensionOutlinedIcon,
        audience: 'platform',
        defaultOrder: 3,
      },
      {
        id: 'platform-features',
        label: 'Feature Flags',
        translationKey: 'nav.platformFeatures',
        path: '/super-admin/features',
        icon: ToggleOnOutlinedIcon,
        audience: 'platform',
        defaultOrder: 4,
      },
      {
        id: 'platform-users',
        label: 'Platform Administrators',
        translationKey: 'nav.platformUsers',
        path: '/super-admin/users',
        icon: PeopleAltOutlinedIcon,
        audience: 'platform',
        defaultOrder: 5,
      },
      {
        id: 'platform-coverage-audit',
        label: 'Docs Coverage Audit',
        translationKey: 'nav.platformCoverageAudit',
        path: '/super-admin/documentation/coverage',
        icon: AssessmentOutlinedIcon,
        audience: 'platform',
        defaultOrder: 6,
      },
    ],
  },
  {
    id: 'platform-qa-group',
    label: 'QA & Testing',
    translationKey: 'nav.platformQaGroup',
    icon: FactCheckOutlinedIcon,
    audience: 'platform',
    defaultOrder: 104,
    children: [
      {
        id: 'platform-qa-center',
        label: 'Overview',
        translationKey: 'nav.platformQaOverview',
        path: '/super-admin/qa',
        icon: FactCheckOutlinedIcon,
        audience: 'platform',
        defaultOrder: 1,
      },
      {
        id: 'platform-qa-suites',
        label: 'Test Suites',
        translationKey: 'nav.platformQaSuites',
        path: '/super-admin/qa/suites',
        icon: ScienceOutlinedIcon,
        audience: 'platform',
        defaultOrder: 2,
      },
      {
        id: 'platform-qa-runs',
        label: 'Test Runs',
        translationKey: 'nav.platformQaRuns',
        path: '/super-admin/qa/runs',
        icon: HistoryOutlinedIcon,
        audience: 'platform',
        defaultOrder: 3,
      },
      {
        id: 'platform-qa-coverage',
        label: 'Test Coverage',
        translationKey: 'nav.platformQaCoverage',
        path: '/super-admin/qa/coverage',
        icon: AssessmentOutlinedIcon,
        audience: 'platform',
        defaultOrder: 4,
      },
      {
        id: 'platform-qa-gates',
        label: 'Release Readiness',
        translationKey: 'nav.platformQaGates',
        path: '/super-admin/qa/quality-gates',
        icon: SpeedOutlinedIcon,
        audience: 'platform',
        defaultOrder: 5,
      },
    ],
  },
  {
    id: 'platform-reports-group',
    label: 'Analytics & Reports',
    translationKey: 'nav.platformReportsGroup',
    icon: BarChartOutlinedIcon,
    audience: 'platform',
    defaultOrder: 105,
    children: [
      {
        id: 'platform-analytics',
        label: 'Platform Analytics',
        translationKey: 'nav.platformAnalytics',
        path: '/super-admin/analytics',
        icon: InsightsOutlinedIcon,
        audience: 'platform',
        defaultOrder: 1,
      },
    ],
  },
  {
    id: 'platform-system-group',
    label: 'System & Security',
    translationKey: 'nav.platformSystemGroup',
    icon: SettingsOutlinedIcon,
    audience: 'platform',
    defaultOrder: 106,
    children: [
      {
        id: 'platform-system-health',
        label: 'System Health',
        translationKey: 'nav.platformSystemHealth',
        path: '/super-admin/system-health',
        icon: DnsOutlinedIcon,
        audience: 'platform',
        defaultOrder: 1,
      },
      {
        id: 'platform-audit-logs',
        label: 'Audit Logs',
        translationKey: 'nav.platformAuditLogs',
        path: '/super-admin/audit-logs',
        icon: HistoryOutlinedIcon,
        audience: 'platform',
        defaultOrder: 2,
      },
      {
        id: 'platform-notifications',
        label: 'Alerts & Notifications',
        translationKey: 'nav.platformNotifications',
        path: '/super-admin/notifications',
        icon: NotificationsNoneOutlinedIcon,
        audience: 'platform',
        defaultOrder: 3,
      },
      {
        id: 'platform-settings',
        label: 'Global Settings',
        translationKey: 'nav.platformSettings',
        path: '/super-admin/settings',
        icon: SettingsOutlinedIcon,
        audience: 'platform',
        defaultOrder: 4,
      },
      {
        id: 'platform-data-cleanup',
        label: 'Data Cleanup',
        translationKey: 'nav.platformDataCleanup',
        path: '/super-admin/data-cleanup',
        icon: DeleteSweepOutlinedIcon,
        audience: 'platform',
        defaultOrder: 5,
      },
      {
        id: 'platform-google-calendar',
        label: 'Google Calendar Integration',
        translationKey: 'nav.platformGoogleCalendar',
        path: '/super-admin/settings/google-calendar',
        icon: CalendarTodayOutlinedIcon,
        audience: 'platform',
        defaultOrder: 6,
      },
      {
        id: 'platform-maintenance',
        label: 'Database Maintenance',
        translationKey: 'nav.platformMaintenance',
        path: '/super-admin/maintenance',
        icon: CleaningServicesOutlinedIcon,
        audience: 'platform',
        defaultOrder: 7,
      },
    ],
  },
];

export interface SocietyNavigationItemPreference {
  menuId: string;
  order: number;
  visible?: boolean;
  children?: SocietyNavigationItemPreference[];
}

export interface SocietyNavigationPreferenceData {
  version: number;
  mainNavigation: SocietyNavigationItemPreference[];
  secondaryNavigation?: SocietyNavigationItemPreference[];
}

export interface MenuVisibilityContext {
  isSuperAdmin: boolean;
  licenseLocked?: boolean;
  societyType?: string;
  propertyStructure?: PropertyStructureConfig;
  propertyTerminology?: ResolvedPropertyTerminology;
  propertyPluralLabel?: string;
  hasFeature?: (featureKey: string) => boolean;
  can: (permission: string | string[]) => boolean;
  hasAnyPermission: (permission: string[]) => boolean;
  hasRole: (role: string | string[]) => boolean;
  menuPreferences?: SocietyNavigationPreferenceData | null;
}

function isMenuItemGated(item: MenuItemConfig): boolean {
  return Boolean(item.permission || item.anyOf?.length || item.roles?.length);
}

function itemAudience(item: MenuItemConfig): 'platform' | 'society' | 'both' {
  if (item.audience) return item.audience;
  if (item.roles?.includes('SUPER_ADMIN')) return 'platform';
  return 'society';
}

function isMenuItemVisible(item: MenuItemConfig, ctx: MenuVisibilityContext): boolean {
  const audience = itemAudience(item);
  if (ctx.isSuperAdmin) {
    if (audience === 'society') return false;
    return audience === 'platform' || audience === 'both';
  }
  if (audience === 'platform') return false;

  // Society feature toggle check (Super Admin can disable modules for this society)
  if (item.featureKey && ctx.hasFeature && !ctx.hasFeature(item.featureKey)) {
    return false;
  }

  if (item.strictRoles && item.roles?.length) {
    return ctx.hasRole(item.roles);
  }

  // Strict watchman role isolation: When user has WATCHMAN and no committee/admin roles
  const isDedicatedWatchman =
    ctx.hasRole('WATCHMAN') &&
    !ctx.hasRole(['PRESIDENT', 'VICE_PRESIDENT', 'GENERAL_SECRETARY', 'TREASURER', 'SUPER_ADMIN']);
  if (isDedicatedWatchman) {
    return Boolean(
      item.path?.startsWith('/watchman') ||
      item.id === 'security-desk-portal' ||
      item.id === 'security-admin'
    );
  }

  // Security Desk & Watchman Gate Desk are strictly for WATCHMAN role only (not Society or Board members)
  if ((item.id === 'visitors' || item.id === 'security-desk-portal') && !ctx.hasRole('WATCHMAN')) {
    return false;
  }

  if (!isMenuItemGated(item)) return true;

  if (item.roles?.length && ctx.hasRole(item.roles)) return true;
  if (item.anyOf?.length && ctx.hasAnyPermission(item.anyOf)) return true;
  if (item.permission && ctx.can(item.permission)) return true;

  return false;
}

/**
 * Authoritative Navigation Resolver.
 * Pipeline:
 * 1. Menu Registry (Immutable definitions)
 * 2. Property Structure / Capability Filter (e.g. Roads, Blocks, Plots, etc.)
 * 3. Feature Entitlement Filter (with parent cascade)
 * 4. RBAC Permission & Role Filter
 * 5. Society Menu Preferences (custom order & visibility)
 * 6. Final synchronized navigation for Desktop & Mobile
 */
export function resolveNavigation(
  items: MenuItemConfig[],
  ctx: MenuVisibilityContext,
): MenuItemConfig[] {
  const visible: MenuItemConfig[] = [];
  const locked = Boolean(ctx.licenseLocked) && !ctx.isSuperAdmin;

  for (const item of items) {
    if (locked && !item.whenLocked && !item.children?.length) continue;
    if (!locked && item.whenLockedOnly) continue;

    // Property capability check (e.g. society-blocks requiring block or tower)
    if (item.propertyCapability && ctx.propertyStructure) {
      const caps = Array.isArray(item.propertyCapability)
        ? item.propertyCapability
        : [item.propertyCapability];
      const hasCap = caps.some((cap) => {
        if (cap === 'road') return ctx.propertyStructure?.roadNumber;
        if (cap === 'block') return ctx.propertyStructure?.block;
        if (cap === 'tower') return ctx.propertyStructure?.tower;
        if (cap === 'flat') return ctx.propertyStructure?.flat;
        if (cap === 'house') return ctx.propertyStructure?.house;
        if (cap === 'plot') return ctx.propertyStructure?.plot;
        return false;
      });
      if (!hasCap) continue;
    }

    // Skip standalone 'license-locked' fallback if /settings/license is already present in visible items
    if (item.id === 'license-locked') {
      const alreadyHasLicense = visible.some(
        (v) =>
          v.path === '/settings/license' ||
          v.children?.some((c) => c.path === '/settings/license'),
      );
      if (alreadyHasLicense) continue;
    }

    if (!isMenuItemVisible(item, ctx)) continue;

    let itemLabel = item.label;
    if (item.id === 'society-houses') {
      itemLabel = ctx.propertyTerminology?.plural || ctx.propertyPluralLabel || item.label;
    } else if (item.id === 'society-blocks' && ctx.propertyStructure) {
      if (ctx.propertyStructure.tower && !ctx.propertyStructure.block) {
        itemLabel = ctx.propertyTerminology?.towerLabel ? `${ctx.propertyTerminology.towerLabel}s` : 'Towers';
      } else if (ctx.propertyStructure.block && !ctx.propertyStructure.tower) {
        itemLabel = ctx.propertyTerminology?.blockLabel ? `${ctx.propertyTerminology.blockLabel}s` : 'Blocks';
      }
    }

    let itemPath = item.path;
    if (item.id === 'dashboard') {
      itemPath = ctx.isSuperAdmin ? '/super-admin' : '/dashboard';
    }

    if (item.children?.length) {
      const visibleChildren = resolveNavigation(item.children, ctx);
      if (!visibleChildren.length) continue;
      visible.push({ ...item, label: itemLabel, path: itemPath, children: visibleChildren });
    } else {
      visible.push({ ...item, label: itemLabel, path: itemPath });
    }
  }

  // Apply custom navigation preferences if present and not in Super Admin view
  if (!ctx.isSuperAdmin && ctx.menuPreferences?.mainNavigation?.length) {
    const prefMap = new Map(
      ctx.menuPreferences.mainNavigation.map((p) => [p.menuId, p]),
    );

    // Filter hidden items (unless locked)
    const allowedVisible = visible.filter((item) => {
      if (locked && item.whenLocked) return true;
      const pref = prefMap.get(item.id);
      if (pref && pref.visible === false) return false;
      return true;
    });

    // Sort by custom order, preserving unconfigured/future modules at their default order
    allowedVisible.sort((a, b) => {
      const orderA = prefMap.get(a.id)?.order ?? (a.defaultOrder ?? 999);
      const orderB = prefMap.get(b.id)?.order ?? (b.defaultOrder ?? 999);
      return orderA - orderB;
    });

    // Sort children if child preferences exist
    for (const item of allowedVisible) {
      if (item.children?.length) {
        const parentPref = prefMap.get(item.id);
        if (parentPref?.children?.length) {
          const childPrefMap = new Map(parentPref.children.map((c) => [c.menuId, c]));
          const sortedChildren = item.children.filter((c) => {
            const cp = childPrefMap.get(c.id);
            if (cp && cp.visible === false) return false;
            return true;
          });
          sortedChildren.sort((a, b) => {
            const ordA = childPrefMap.get(a.id)?.order ?? (a.defaultOrder ?? 999);
            const ordB = childPrefMap.get(b.id)?.order ?? (b.defaultOrder ?? 999);
            return ordA - ordB;
          });
          item.children = sortedChildren;
        }
      }
    }

    return allowedVisible;
  }

  return visible;
}

/** Recursively filters the menu tree, dropping parents whose children are all hidden. */
export function getVisibleMenuItems(
  items: MenuItemConfig[],
  ctx: MenuVisibilityContext,
): MenuItemConfig[] {
  return resolveNavigation(items, ctx);
}

/**
 * Single source of truth for canonical English navigation display names.
 * Ensures zero technical identifier leak even if translations or custom definitions are missing.
 */
export const CANONICAL_NAV_LABELS: Record<string, string> = {
  dashboard: 'Home',
  society: 'My Society',
  'society-settings': 'Society Details',
  'society-blocks': 'Towers / Blocks',
  'society-houses': 'Houses / Flats',
  'society-residents': 'Residents',
  'society-staff': 'Society Staff',
  imports: 'Import Data',
  finance: 'Payments & Dues',
  'finance-payments': 'My Payments & Dues',
  'finance-collections': 'Collections',
  'finance-society-fund': 'Society Fund',
  'finance-special-collections': 'Special Collections',
  'finance-corpus-fund': 'Corpus Fund',
  'finance-expenses': 'Expenses',
  'finance-expense-groups': 'Expense Categories',
  'finance-water-tanker': 'Water Tankers',
  'finance-reimbursements': 'Reimbursements',
  'finance-banking': 'Bank & Accounts',
  'finance-statements': 'Statements & Receipts',
  'finance-reports': 'Financial Reports',
  'finance-reconciliation': 'Bank Matching',
  'security-admin': 'Security & Gate',
  'gate-passes': 'Gate Passes',
  'security-dashboard': 'Gate Overview',
  'security-gates': 'Gate Entry',
  'security-shifts': 'Guard Shifts',
  'security-notification-settings': 'Gate Rules',
  'security-desk-portal': 'Security Guard Desk',
  visitors: 'Visitors & Guests',
  'security-cc-cameras': 'CC Cameras',
  community: 'Community',
  notices: 'Notice Board',
  forum: 'Community Forum',
  'events-list': 'Events',
  'events-calendar': 'Event Calendar',
  invitations: 'RSVPs & Invites',
  whatsapp: 'WhatsApp Updates',
  notifications: 'Notifications',
  operations: 'Services & Help',
  complaints: 'Issues & Helpdesk',
  'services-requests': 'Service Requests',
  'services-directory': 'Local Directory',
  amenities: 'Facilities',
  meetings: 'Meetings & AGM',
  documents: 'Document Vault',
  governance: 'Governance & Elections',
  polls: 'Resident Polls',
  elections: 'Elections',
  'governance-petitions': 'Petitions',
  'governance-resignation': 'Resignations',
  administration: 'Admin',
  'admin-license': 'Software License',
  'admin-invoices': 'Subscription Invoices',
  'admin-roles': 'Users & Roles',
  'admin-letterhead': 'Letterhead & Branding',
  'admin-integrations': 'SMS & WhatsApp',
  'admin-ai-config': 'AI Assistant',
  'admin-navigation': 'Menu Settings',
  'admin-audit': 'System Activity',
  'documentation-group': 'Documents & Help',
  'society-support-desk': 'Help & Support',
  'docs-knowledge-base': 'Knowledge Base',
  'docs-role-matrix': 'Permission Matrix',
  'docs-qa-hub': 'QA Hub',
  'docs-test-explorer': 'Test Explorer',
  'license-locked': 'Software License',
  'platform-societies-group': 'Societies & Tenants',
  'platform-societies': 'All Societies',
  'platform-societies-new': 'Register Society',
  'platform-society-types': 'Society Types & Taxonomy',
  'platform-trials': 'Free Trials & Onboarding',
  'platform-subscriptions': 'Subscriptions',
  'platform-licenses': 'Software Licenses',
  'platform-plans': 'Subscription Plans',
  'platform-invoices': 'Billing & Invoices',
  'platform-pricing-overrides': 'Pricing Overrides',
  'platform-coupons': 'Coupons & Discounts',
  'platform-promotion-group': 'Growth & Promotion',
  'platform-promotion-dashboard': 'Growth Dashboard',
  'platform-promotion-prospects': 'Prospects & Leads',
  'platform-promotion-campaigns': 'Campaigns',
  'platform-promotion-templates': 'Message Templates',
  'platform-promotion-sender-profiles': 'Sender Profiles',
  'platform-promotion-email': 'Email Marketing',
  'platform-promotion-whatsapp': 'WhatsApp Broadcasts',
  'platform-promotion-sms': 'SMS Marketing',
  'platform-promotion-other': 'Other Channels',
  'platform-referrals': 'Affiliates & Referrals',
  'platform-marketing': 'Marketing Personnel',
  'platform-promotion-analytics': 'Promotion Analytics',
  'platform-promotion-settings': 'Marketing Settings',
  'platform-management-group': 'Platform & Support',
  'platform-website': 'Website CMS & Branding',
  'platform-support-tickets': 'Support Tickets Desk',
  'platform-feature-requests': 'Feature Requests',
  'platform-features': 'Feature Flags',
  'platform-users': 'Platform Administrators',
  'platform-coverage-audit': 'Docs Coverage Audit',
  'platform-reports-group': 'Analytics & Reports',
  'platform-analytics': 'Platform Analytics',
  'platform-system-group': 'System & Security',
  'platform-system-health': 'System Health',
  'platform-audit-logs': 'Audit Logs',
  'platform-notifications': 'Alerts & Notifications',
  'platform-settings': 'Global Settings',
  'platform-data-cleanup': 'Data Cleanup',
  'platform-google-calendar': 'Google Calendar Integration',
  'platform-maintenance': 'Database Maintenance',
};

/** Converts a kebab-case technical identifier into camelCase for key lookup (e.g. finance-payments -> financePayments). */
function kebabToCamel(str: string): string {
  return str.replace(/-([a-z0-9])/g, (_, char) => char.toUpperCase());
}

/**
 * Resolves human-readable, localized display label for any menu item.
 * Guarantee: NEVER falls back to technical identifiers, IDs, slugs or database keys.
 */
export function getNavLabel(
  t: (key: string, params?: Record<string, string | number>) => string,
  item: MenuItemConfig,
  currentLanguage: SupportedLanguage = 'en',
): string {
  // 1. Dynamic Property Terminology override (e.g. Villas, Plots, Towers)
  if (item.id === 'society-houses' && item.label && item.label !== 'Houses') {
    const localizedTerm = resolveLocalizedPropertyTerm(item.label, currentLanguage);
    if (localizedTerm) return localizedTerm;
  }
  if (item.id === 'society-blocks' && item.label && item.label !== 'Blocks / Towers') {
    const localizedTerm = resolveLocalizedPropertyTerm(item.label, currentLanguage);
    if (localizedTerm) return localizedTerm;
  }

  // 2. Look up explicit translationKey
  if (item.translationKey) {
    const translated = t(item.translationKey);
    if (
      translated &&
      translated !== item.translationKey &&
      !translated.startsWith('nav.') &&
      !translated.startsWith('navigation.') &&
      !/^[a-z]+-[a-z0-9-]+$/.test(translated)
    ) {
      return translated;
    }
  }

  // 3. Look up camelCase translation key from item.id (e.g. nav.financePayments)
  const camelKey = `nav.${kebabToCamel(item.id)}`;
  const camelTranslated = t(camelKey);
  if (
    camelTranslated &&
    camelTranslated !== camelKey &&
    !camelTranslated.startsWith('nav.') &&
    !/^[a-z]+-[a-z0-9-]+$/.test(camelTranslated)
  ) {
    return camelTranslated;
  }

  // 4. Look up legacy raw id translation key (e.g. nav.society)
  const legacyKey = `nav.${item.id}`;
  const legacyTranslated = t(legacyKey);
  if (
    legacyTranslated &&
    legacyTranslated !== legacyKey &&
    !legacyTranslated.startsWith('nav.') &&
    !/^[a-z]+-[a-z0-9-]+$/.test(legacyTranslated)
  ) {
    return legacyTranslated;
  }

  // 5. Canonical English registry lookup
  if (CANONICAL_NAV_LABELS[item.id]) {
    return CANONICAL_NAV_LABELS[item.id];
  }

  // 6. Safe human-readable label fallback if not matching a technical identifier pattern
  if (item.label && item.label !== item.id && !/^[a-z]+-[a-z0-9-]+$/.test(item.label)) {
    return item.label;
  }

  // 7. Ultimate safety fallback: convert technical slug to Title Case human text (NEVER return raw slug)
  return item.id
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
